import axios from "axios"
import React, { useState, useMemo } from "react" // Tambahkan useMemo
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip"
import { Navigation } from "@/components/public/layout/Navigation"
import { useNavigate } from "react-router-dom";
import {
    Users, User, Eye, EyeOff, CheckCircle, Circle,
    Shield, ShieldHalf, ShieldCheck, Lock, XCircle, Loader2, UserPlus, UserCheck, AlertCircle,
    Info,
} from "lucide-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import RegistrationHero from "@/components/registrations/RegistrationHero"
import RegistrationFooter from "@/components/registrations/RegistrationFooter"
import RegistrationBenefit from "@/components/registrations/RegistrationBenefit"
import { registerUser } from "@/services/register";
import { useTranslation } from "react-i18next"; // 1. Import i18next
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"

import ClothingSizeModal from "@/components/ClothingSizeModal"

import { ITEMS_GENDER, ITEMS_BLOOD_TYPE, ITEMS_CLOTHES_SIZE, ITEMS_INSTITUTION } from "@/constants/options";

type ValidationErrors = Record<string, string[]>;

export default function RegistrationPage() {
    const { t } = useTranslation('registration');
    const navigate = useNavigate();

    // Fungsi untuk kekuatan password disesuaikan dengan translasi
    const getPasswordStrength = (password: string) => {
        let score = 0
        if (password.length >= 8) score++
        if (/[A-Z]/.test(password)) score++
        if (/[a-z]/.test(password)) score++
        if (/\d/.test(password)) score++
        if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++

        if (score <= 2) return { label: t('registration.password_rules.weak'), color: "bg-red-500", text: "text-red-600" }
        if (score === 3 || score === 4) return { label: t('registration.password_rules.medium'), color: "bg-yellow-500", text: "text-yellow-600" }
        if (score === 5) return { label: t('registration.password_rules.strong'), color: "bg-green-500", text: "text-green-600" }
        return { label: "", color: "bg-gray-300", text: "text-gray-400" }
    }

    const [formData, setFormData] = useState({
        registrationType: "personal", // default
        fullName: "",
        email: "",
        password: "",
        password_confirmation: "",
        phone: "",
        institution: "",
        position: "",
        institution_other: "",
        gender: "",
        blood_type: "",
        clothing_size: "",
        address: "",
        city: "",
        postalCode: "",
        agreeToTerms: false,
    })

    const [showPassword, setShowPassword] = useState(false)
    const [showpassword_confirmation, setShowpassword_confirmation] = useState(false)

    // State untuk UI
    const [loading, setLoading] = useState(false)
    const [serverError, setServerError] = useState<string | null>(null)
    const [successMessage, setSuccessMessage] = useState<string | null>(null)
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

    // Update handle input agar error hilang saat user mengetik
    const handleInputChange = (field: string, value: string | boolean) => {
        setFormData((prev) => ({ ...prev, [field]: value }))

        // Hapus error spesifik field tersebut jika user mengetik
        if (validationErrors[field]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    }

    // 3. Password rules disesuaikan teksnya
    const passwordRules = useMemo(() => [
        { test: (p: string) => p.length >= 8, text: t('registration.password_rules.min_char') },
        { test: (p: string) => /[A-Z]/.test(p), text: t('registration.password_rules.uppercase') },
        { test: (p: string) => /[a-z]/.test(p), text: t('registration.password_rules.lowercase') },
        { test: (p: string) => /\d/.test(p), text: t('registration.password_rules.number') },
        { test: (p: string) => /[!@#$%^&*(),.?":{}|<>]/.test(p), text: t('registration.password_rules.symbol') },
    ], [t]);

    const passwordValid = passwordRules.every((r) => r.test(formData.password))
    const passwordsMatch =
        formData.password.length > 0 &&
        formData.password_confirmation.length > 0 &&
        formData.password === formData.password_confirmation

    const submitDisabled =
        !formData.registrationType ||
        !formData.fullName ||
        !formData.email ||
        !formData.password ||
        !formData.password_confirmation ||
        !passwordsMatch ||
        !passwordValid ||
        !formData.phone ||
        !formData.institution ||
        !formData.gender ||
        !formData.blood_type ||
        !formData.clothing_size ||
        !formData.agreeToTerms;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setServerError(null);
        setValidationErrors({});
        setSuccessMessage(null);

        // ... validasi password client side ...
        if (!passwordValid) {
            setServerError(t('registration.error_password_criteria'));
            return;
        }
        if (!passwordsMatch) {
            setServerError(t('registration.error_mismatch'));
            return;
        }

        const institutionToSubmit = formData.institution === "other" ? formData.institution_other : formData.institution;

        setLoading(true);
        try {
            const data = await registerUser({
                registrationType: formData.registrationType,
                fullName: formData.fullName,
                email: formData.email,
                password: formData.password,
                password_confirmation: formData.password_confirmation,
                phone: formData.phone,
                institution: institutionToSubmit,
                position: formData.position,
                gender: formData.gender,
                blood_type: formData.blood_type,
                clothing_size: formData.clothing_size,
                address: formData.address,
                city: formData.city,
                postalCode: formData.postalCode,
                agreeToTerms: formData.agreeToTerms,
            });

            // Jika masuk sini, berarti status 200/201 (Sukses)
            setSuccessMessage(data.message || t('registration.success_msg'));

            // Reset form
            setFormData({
                registrationType: "personal",
                fullName: "",
                email: "",
                password: "",
                password_confirmation: "",
                phone: "",
                institution: "",
                institution_other: "",
                position: "",
                gender: "",
                blood_type: "",
                clothing_size: "",
                address: "",
                city: "",
                postalCode: "",
                agreeToTerms: false,
            });

            navigate(`/registration/success?email=${encodeURIComponent(formData.email)}`);

        } catch (err: unknown) {
            if (axios.isAxiosError(err) && err.response) {
                const status = err.response.status;
                const responseData = err.response.data;

                // KASUS 1: Validasi Gagal (422)
                if (status === 422) {
                    setValidationErrors(responseData.errors);
                    setServerError(t('registration.error_validation'));
                }
                // KASUS 2: Pendaftaran Ditutup (403) atau Error Server (500) dengan pesan
                else if (status === 403 || (responseData && responseData.message)) {
                    setServerError(responseData.message);
                }
                // KASUS 3: Error lainnya tanpa pesan spesifik
                else {
                    setServerError(t('registration.error_server'));
                }
            } else {
                setServerError(t('registration.error_connection'));
            }
        } finally {
            setLoading(false);
        }
    };

    // Helper untuk menampilkan pesan error di bawah input
    const FieldError = ({ field }: { field: string }) => {
        if (!validationErrors[field]) return null;
        return (
            <div className="text-red-500 text-xs mt-1 flex items-center gap-1 animate-in fade-in slide-in-from-top-1">
                <AlertCircle className="w-3 h-3" />
                <span>{validationErrors[field][0]}</span>
            </div>
        );
    };

    const [isClothingSizeOpen, setIsClothingSizeOpen] = useState(false);

    return (
        <div className="min-h-screen bg-background dark:bg-gray-900">
            <Navigation />

            {/* Hero Section */}
            <RegistrationHero />

            {/* Registration Form Section */}
            <section className="py-8 md:py-16 bg-gradient-to-b from-muted/30 to-background dark:from-gray-900/30 dark:to-gray-900">
                <div className="container mx-auto px-4 max-w-9xl">
                    <div className="grid lg:grid-cols-4 gap-6 md:gap-8">
                        <RegistrationBenefit />

                        <div className="lg:col-span-3 order-1 lg:order-2">
                            <Card className="bg-white/90 shadow-2xl border-0 dark:bg-gray-800/50 dark:border-gray-700 pt-0 text-left">
                                <CardHeader className="bg-gradient-to-r from-primary to-primary dark:from-blue-600 dark:to-purple-600 text-white rounded-t-lg p-8">
                                    <CardTitle className="text-xl md:text-2xl lg:text-3xl flex items-center gap-3">
                                        <Users className="w-6 h-6 md:w-8 md:h-8" />
                                        {t('registration.form_title')}
                                    </CardTitle>
                                    <CardDescription className="text-white/90 text-sm md:text-base lg:text-lg">
                                        {t('registration.form_subtitle')}
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="p-4 md:p-6 lg:p-8">
                                    <form onSubmit={handleSubmit} className="space-y-8 md:space-y-10">


                                        {/* === Tipe Pendaftaran === */}
                                        <div className="space-y-6">
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-full">
                                                    <UserPlus className="w-4 h-4 md:w-5 md:h-5 text-blue-600 dark:text-blue-400" />
                                                </div>
                                                <h3 className="text-lg md:text-xl font-bold text-primary dark:text-blue-400">
                                                    {t('registration.type_title')}
                                                </h3>
                                            </div>
                                            <div className="section-divider dark:bg-gray-600"></div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {/* PERSONAL */}
                                                <div
                                                    onClick={() => handleInputChange("registrationType", "personal")}
                                                    className={`cursor-pointer rounded-xl border-2 p-5 transition-all duration-200 ${formData.registrationType === "personal"
                                                        ? "border-blue-600 bg-blue-50 dark:bg-blue-900/20"
                                                        : "border-gray-300 dark:border-gray-600 hover:border-blue-400"
                                                        }`}
                                                >
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <UserCheck className={`w-5 h-5 ${formData.registrationType === "personal" ? "text-blue-600" : "text-gray-500"}`} />
                                                        <h4 className="font-semibold">{t('registration.personal.title')}</h4>
                                                    </div>
                                                    <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                                                        {t('registration.personal.desc')}
                                                    </p>
                                                </div>

                                                {/* KOLEKTIF */}
                                                <div
                                                    onClick={() => handleInputChange("registrationType", "kolektif")}
                                                    className={`cursor-pointer rounded-xl border-2 p-5 transition-all duration-200 ${formData.registrationType === "kolektif"
                                                        ? "border-purple-600 bg-purple-50 dark:bg-purple-900/20"
                                                        : "border-gray-300 dark:border-gray-600 hover:border-purple-400"
                                                        }`}
                                                >
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <Users className={`w-5 h-5 ${formData.registrationType === "kolektif" ? "text-purple-600" : "text-gray-500"}`} />
                                                        <h4 className="font-semibold">{t('registration.kolektif.title')}</h4>
                                                    </div>
                                                    <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                                                        {t('registration.kolektif.desc')}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="space-y-4 md:space-y-6">
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="bg-primary/10 dark:bg-blue-500/20 p-2 rounded-full">
                                                    <Users className="w-4 h-4 md:w-5 md:h-5 text-primary dark:text-blue-400" />
                                                </div>
                                                <h3 className="text-lg md:text-xl font-bold text-primary dark:text-blue-400">{t('registration.personal_data')}</h3>
                                            </div>
                                            <div className="section-divider dark:bg-gray-600"></div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                                <div className="space-y-3">
                                                    <Label htmlFor="fullName" className="text-sm md:text-base font-semibold text-foreground dark:text-gray-200">
                                                        {t('registration.labels.full_name')} <span className="text-red-500">*</span>
                                                    </Label>
                                                    <Input
                                                        id="fullName"
                                                        value={formData.fullName}
                                                        onChange={(e) => handleInputChange("fullName", e.target.value)}
                                                        placeholder={t('registration.labels.full_name_placeholder')}
                                                        className={`input-enhanced h-10 md:h-12 text-sm md:text-base dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 ${validationErrors.fullName ? 'border-red-500 ring-red-200 focus-visible:ring-red-500' : 'dark:border-gray-600'}`}
                                                        required
                                                    />
                                                    <FieldError field="fullName" />
                                                </div>
                                                <div className="space-y-3">
                                                    <Label htmlFor="phone" className="text-sm md:text-base font-semibold text-foreground dark:text-gray-200">
                                                        {t('registration.labels.phone')} <span className="text-red-500">*</span>
                                                    </Label>
                                                    <Input
                                                        id="phone"
                                                        value={formData.phone}
                                                        onChange={(e) => handleInputChange("phone", e.target.value)}
                                                        placeholder="+62 812 3456 7890"
                                                        className={`input-enhanced h-10 md:h-12 text-sm md:text-base dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 ${validationErrors.phone ? 'border-red-500 ring-red-200 focus-visible:ring-red-500' : 'dark:border-gray-600'}`}
                                                        required
                                                    />
                                                    <FieldError field="phone" />
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                                <div className="space-y-3">
                                                    <Label htmlFor="institution" className="text-sm md:text-base font-semibold text-foreground dark:text-gray-200">
                                                        {t('registration.labels.institution')} <span className="text-red-500">*</span>
                                                    </Label>
                                                    <Select value={formData.institution} onValueChange={(value) => handleInputChange("institution", value)}>
                                                        <SelectTrigger style={{ height: '45px' }} className={`input-enhanced w-full text-sm dark:bg-gray-700 dark:text-white ${validationErrors.institution ? 'border-red-500 ring-red-200 focus-visible:ring-red-500' : 'dark:border-gray-600'}`}>
                                                            <SelectValue placeholder={t('registration.labels.institution_placeholder')} />
                                                        </SelectTrigger>
                                                        <SelectContent className="dark:bg-gray-800 dark:border-gray-700">
                                                            <SelectGroup>
                                                                {ITEMS_INSTITUTION.map((item) => (
                                                                    <SelectItem key={item.value} value={item.value}>
                                                                        {item.label}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectGroup>
                                                        </SelectContent>
                                                    </Select>
                                                    <FieldError field="institution" />
                                                </div>
                                                {formData.institution === "other" && (
                                                    <div className="space-y-3">
                                                        <Label htmlFor="institution_other" className="text-sm md:text-base font-semibold text-foreground dark:text-gray-200">
                                                            {t('registration.labels.institution_other')} <span className="text-red-500">*</span>
                                                        </Label>
                                                        <Input
                                                            id="institution_other"
                                                            value={formData.institution_other}
                                                            onChange={(e) => handleInputChange("institution_other", e.target.value)}
                                                            placeholder={t('registration.labels.institution_other_placeholder')}
                                                            className={`input-enhanced h-10 md:h-12 text-sm md:text-base dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 ${validationErrors.institution_other ? 'border-red-500 ring-red-200 focus-visible:ring-red-500' : 'dark:border-gray-600'}`}
                                                            required
                                                        />
                                                        <FieldError field="institution_other" />
                                                    </div>
                                                )}
                                                <div className={`space-y-3 ${formData.institution === "other" ? "md:col-span-2" : ""}`}>
                                                    <Label htmlFor="position" className="text-sm md:text-base font-semibold text-foreground dark:text-gray-200">
                                                        {t('registration.labels.position')}
                                                    </Label>
                                                    <Input
                                                        id="position"
                                                        value={formData.position}
                                                        onChange={(e) => handleInputChange("position", e.target.value)}
                                                        placeholder={t('registration.labels.position_placeholder')}
                                                        className={`input-enhanced h-10 md:h-12 text-sm md:text-base dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 ${validationErrors.position ? 'border-red-500 ring-red-200 focus-visible:ring-red-500' : 'dark:border-gray-600'}`}
                                                    />
                                                    <FieldError field="position" />
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                                {/* Baris 1: Gender & Blood Type */}
                                                <div className="grid grid-cols-2 gap-4 md:gap-6">
                                                    {/* Gender */}
                                                    <div className="space-y-3">
                                                        <Label htmlFor="gender" className="text-sm md:text-base font-semibold text-foreground dark:text-gray-200">
                                                            {t('registration.labels.gender')} 
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <button
                                                                        type="button"
                                                                        className="rounded-full p-1 text-blue-600 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-white/10 transition"
                                                                        aria-label={t('registration.labels.info')}
                                                                    >
                                                                        <Info size={14} />
                                                                    </button>
                                                                </TooltipTrigger>
                                                                <TooltipContent side="top" className="max-w-[220px]">
                                                                    <div className="space-y-2 text-left">
                                                                        <p className="text-xs leading-relaxed">
                                                                            {t('registration.tooltips.gender_short')}
                                                                        </p>
                                                                    </div>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                            <span className="text-red-500">*</span>
                                                        </Label>
                                                        <Select value={formData.gender} onValueChange={(value)=> handleInputChange("gender", value)}>
                                                            <SelectTrigger style={{ height: '45px' }} className={`input-enhanced w-full text-sm dark:bg-gray-700 dark:text-white ${validationErrors.gender ? 'border-red-500 ring-red-200 focus-visible:ring-red-500' : 'dark:border-gray-600' }`}>
                                                                <SelectValue placeholder={t('registration.labels.gender_placeholder')} />
                                                            </SelectTrigger>
                                                            <SelectContent className="dark:bg-gray-800 dark:border-gray-700">
                                                                <SelectGroup>
                                                                    {ITEMS_GENDER.map((item) => (
                                                                    <SelectItem key={item.value} value={item.value}
                                                                        className="dark:text-gray-200 dark:focus:bg-gray-700">
                                                                        {item.label}
                                                                    </SelectItem>
                                                                    ))}
                                                                </SelectGroup>
                                                            </SelectContent>
                                                        </Select>
                                                        <FieldError field="gender" />
                                                    </div>

                                                    {/* Blood Type */}
                                                    <div className="space-y-3">
                                                        <Label htmlFor="blood_type"
                                                            className="text-sm md:text-base font-semibold text-foreground dark:text-gray-200">
                                                            {t('registration.labels.blood_type')} 
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <button
                                                                        type="button"
                                                                        className="rounded-full p-1 text-blue-600 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-white/10 transition"
                                                                        aria-label={t('registration.labels.info')}
                                                                    >
                                                                        <Info size={14} />
                                                                    </button>
                                                                </TooltipTrigger>
                                                                <TooltipContent side="top" className="max-w-[220px]">
                                                                    <div className="space-y-2 text-left">
                                                                        <p className="text-xs leading-relaxed">
                                                                            {t('registration.tooltips.blood_type_short')}
                                                                        </p>
                                                                    </div>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                            <span className="text-red-500">*</span>
                                                        </Label>


                                                        <Select value={formData.blood_type} onValueChange={(value)=> handleInputChange("blood_type", value)}>
                                                            <SelectTrigger style={{ height: '45px' }} className={`input-enhanced w-full text-sm dark:bg-gray-700 dark:text-white ${validationErrors.blood_type ? 'border-red-500 ring-red-200 focus-visible:ring-red-500' : 'dark:border-gray-600' }`}>
                                                                <SelectValue placeholder={t('registration.labels.blood_type')} />
                                                            </SelectTrigger>
                                                            <SelectContent className="dark:bg-gray-800 dark:border-gray-700">
                                                                <SelectGroup>
                                                                    {ITEMS_BLOOD_TYPE.map((item) => (
                                                                    <SelectItem key={item.value} value={item.value}
                                                                        className="dark:text-gray-200 dark:focus:bg-gray-700">
                                                                        {item.label}
                                                                    </SelectItem>
                                                                    ))}
                                                                </SelectGroup>
                                                            </SelectContent>
                                                        </Select>
                                                        <FieldError field="blood_type" />
                                                    </div>
                                                </div>

                                                {/* Baris 2: Clothing Size (Akan menyesuaikan grid md:grid-cols-2 dengan input lainnya) */}
                                                <div className="space-y-3">
                                                    <div className="flex items-center justify-between">
                                                        <Label htmlFor="clothing_size" className="text-sm md:text-base font-semibold text-foreground dark:text-gray-200">
                                                            {t('registration.labels.clothing_size')} <span className="text-red-500">*</span>
                                                        </Label>

                                                        <button
                                                            type="button"
                                                            onClick={() => setIsClothingSizeOpen(true)}
                                                            className="text-xs md:text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 flex items-center gap-1.5 transition-colors font-medium underline underline-offset-2"
                                                        >
                                                            <Info size={14} />
                                                            {t('registration.labels.clothing_size_table')}
                                                        </button>
                                                    </div>

                                                    <Select value={formData.clothing_size} onValueChange={(value) => handleInputChange("clothing_size", value)}>
                                                        <SelectTrigger
                                                            style={{ height: '45px' }}
                                                            className={`input-enhanced w-full text-sm dark:bg-gray-700 dark:text-white ${validationErrors.clothing_size ? 'border-red-500 ring-red-200 focus-visible:ring-red-500' : 'dark:border-gray-600'}`}
                                                        >
                                                            <SelectValue placeholder={t('registration.labels.clothing_size_placeholder')} />
                                                        </SelectTrigger>
                                                        <SelectContent className="dark:bg-gray-800 dark:border-gray-700">
                                                            <SelectGroup>
                                                                {ITEMS_CLOTHES_SIZE.map((item) => (
                                                                    <SelectItem key={item.value} value={item.value} className="dark:text-gray-200 dark:focus:bg-gray-700">
                                                                        {item.label}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectGroup>
                                                        </SelectContent>
                                                    </Select>
                                                    <FieldError field="clothing_size" />

                                                    {/* BAGIAN MODAL YANG DIPERBAIKI */}
                                                    <ClothingSizeModal
                                                        isOpen={isClothingSizeOpen}
                                                        onOpenChange={setIsClothingSizeOpen}
                                                    />
                                                </div>
                                            </div>

                                            <div className="space-y-3">
                                                <Label htmlFor="address" className="text-sm md:text-base font-semibold text-foreground dark:text-gray-200">
                                                    {t('registration.labels.address')}
                                                </Label>
                                                <Textarea
                                                    id="address"
                                                    value={formData.address}
                                                    onChange={(e) => handleInputChange("address", e.target.value)}
                                                    placeholder={t('registration.labels.address_placeholder')}
                                                    className={`input-enhanced min-h-[80px] md:min-h-[100px] text-sm md:text-base resize-none dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 ${validationErrors.address ? 'border-red-500 ring-red-200 focus-visible:ring-red-500' : 'dark:border-gray-600'}`}
                                                    rows={4}
                                                />
                                                <FieldError field="address" />
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                                <div className="space-y-3">
                                                    <Label htmlFor="city" className="text-sm md:text-base font-semibold text-foreground dark:text-gray-200">
                                                        {t('registration.labels.city')}
                                                    </Label>
                                                    <Input
                                                        id="city"
                                                        value={formData.city}
                                                        onChange={(e) => handleInputChange("city", e.target.value)}
                                                        placeholder={t('registration.labels.city')}
                                                        className={`input-enhanced h-10 md:h-12 text-sm md:text-base dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 ${validationErrors.city ? 'border-red-500 ring-red-200 focus-visible:ring-red-500' : 'dark:border-gray-600'}`}
                                                    />
                                                    <FieldError field="city" />
                                                </div>
                                                <div className="space-y-3">
                                                    <Label htmlFor="postalCode" className="text-sm md:text-base font-semibold text-foreground dark:text-gray-200">
                                                        {t('registration.labels.postal_code')}
                                                    </Label>
                                                    <Input
                                                        id="postalCode"
                                                        value={formData.postalCode}
                                                        onChange={(e) => handleInputChange("postalCode", e.target.value)}
                                                        placeholder={t('registration.labels.postal_code')}
                                                        className={`input-enhanced h-10 md:h-12 text-sm md:text-base dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 ${validationErrors.postalCode ? 'border-red-500 ring-red-200 focus-visible:ring-red-500' : 'dark:border-gray-600'}`}
                                                    />
                                                    <FieldError field="postalCode" />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="space-y-4 md:space-y-6">
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="bg-accent/10 dark:bg-purple-500/20 p-2 rounded-full">
                                                    <User className="w-4 h-4 md:w-5 md:h-5 text-accent dark:text-purple-400" />
                                                </div>
                                                <h3 className="text-lg md:text-xl font-bold text-primary dark:text-blue-400">
                                                    {t('registration.account.title')}
                                                </h3>
                                            </div>
                                            <div className="section-divider dark:bg-gray-600"></div>
                                            <div className="space-y-3">
                                                <Label htmlFor="email" className="text-sm md:text-base font-semibold text-foreground dark:text-gray-200 mb-0">
                                                    {t('registration.account.email')} <span className="text-red-500">*</span>
                                                </Label>
                                                <span className="text-sm text-yellow-600 block">{t('registration.account.email_note')}</span>
                                                <Input
                                                    id="email"
                                                    type="email"
                                                    value={formData.email}
                                                    onChange={(e) => handleInputChange("email", e.target.value)}
                                                    placeholder="nama@email.com"
                                                    className={`input-enhanced h-10 md:h-12 text-sm md:text-base dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 ${validationErrors.email ? 'border-red-500 ring-red-200 focus-visible:ring-red-500' : 'dark:border-gray-600'}`}
                                                    required
                                                />
                                                <FieldError field="email" />
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                                {/* Password */}
                                                <div className="space-y-3 relative">
                                                    <Label htmlFor="password" className="text-sm md:text-base font-semibold text-foreground dark:text-gray-200">{t('registration.account.password')} <span className="text-red-500">*</span></Label>
                                                    <div className="relative">
                                                        <Input
                                                            id="password"
                                                            type={showPassword ? "text" : "password"}
                                                            value={formData.password}
                                                            onChange={(e) => handleInputChange("password", e.target.value)}
                                                            placeholder={t('registration.account.password_placeholder')}
                                                            className={`input-enhanced h-10 md:h-12 text-sm md:text-base dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 pr-10 ${validationErrors.password ? 'border-red-500 ring-red-200 focus-visible:ring-red-500' : 'dark:border-gray-600'}`}
                                                            required
                                                        />
                                                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-3 flex items-center">
                                                            {showPassword ? <EyeOff className="w-5 h-5 text-gray-500" /> : <Eye className="w-5 h-5 text-gray-500" />}
                                                        </button>
                                                    </div>
                                                    <FieldError field="password" />

                                                    {/* Indikator kekuatan */}
                                                    {formData.password && (
                                                        <div className="mt-3 pl-2">
                                                            {(() => {
                                                                const s = getPasswordStrength(formData.password)
                                                                return (
                                                                    <div className="flex items-center gap-2">
                                                                        <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                                                            <div
                                                                                className={`h-2 ${s.color} transition-all duration-300`}
                                                                                style={{
                                                                                    width: s.label === t('registration.password_rules.weak') ? "33%" : s.label === t('registration.password_rules.medium') ? "66%" : "100%",
                                                                                }}
                                                                            />
                                                                        </div>
                                                                        <span className={`flex items-center gap-1 text-xs font-semibold ${s.text}`}>
                                                                            {s.label === t('registration.password_rules.weak') && <Shield className="w-3.5 h-3.5" />}
                                                                            {s.label === t('registration.password_rules.medium') && <ShieldHalf className="w-3.5 h-3.5" />}
                                                                            {s.label === t('registration.password_rules.strong') && <ShieldCheck className="w-3.5 h-3.5" />}
                                                                            {s.label}
                                                                        </span>
                                                                    </div>
                                                                )
                                                            })()}
                                                        </div>
                                                    )}
                                                    {/* Checklist aturan password */}
                                                    <div className="text-xs space-y-1 pl-2 mt-2">
                                                        {passwordRules.map((rule, idx) => {
                                                            const passed = rule.test(formData.password)
                                                            return (
                                                                <div key={idx} className="flex items-center gap-2">
                                                                    {passed ? (
                                                                        <CheckCircle className="w-4 h-4 text-green-500" />
                                                                    ) : (
                                                                        <Circle className="w-4 h-4 text-gray-400" />
                                                                    )}
                                                                    <span className={passed ? "text-green-600" : "text-gray-500"}>{rule.text}</span>
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                </div>

                                                {/* Konfirmasi Password */}
                                                <div className="space-y-3 relative">
                                                    <Label htmlFor="password_confirmation" className="text-sm md:text-base font-semibold text-foreground dark:text-gray-200">{t('registration.account.confirm_password')} <span className="text-red-500">*</span></Label>
                                                    <div className="relative">
                                                        <Input
                                                            id="password_confirmation"
                                                            type={showpassword_confirmation ? "text" : "password"}
                                                            value={formData.password_confirmation}
                                                            onChange={(e) => handleInputChange("password_confirmation", e.target.value)}
                                                            placeholder={t('registration.account.confirm_password_placeholder')}
                                                            className={`input-enhanced h-10 md:h-12 text-sm md:text-base dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 pr-10 ${validationErrors.password_confirmation ? 'border-red-500 ring-red-200 focus-visible:ring-red-500' : 'dark:border-gray-600'}`}
                                                            required
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowpassword_confirmation(!showpassword_confirmation)}
                                                            className="absolute inset-y-0 right-3 flex items-center"
                                                        >
                                                            {showpassword_confirmation ? <EyeOff className="w-5 h-5 text-gray-500" /> : <Eye className="w-5 h-5 text-gray-500" />}
                                                        </button>
                                                    </div>
                                                    <FieldError field="password_confirmation" />

                                                    {/* Real-time match status */}
                                                    <div className="mt-2 pl-2" aria-live="polite">
                                                        {formData.password_confirmation.length > 0 && (
                                                            passwordsMatch ? (
                                                                <div className="flex items-center gap-2 text-green-600">
                                                                    <CheckCircle className="w-4 h-4" />
                                                                    <span className="text-sm">{t('registration.account.password_match')}</span>
                                                                </div>
                                                            ) : (
                                                                <div className="flex items-center gap-2 text-red-600">
                                                                    <XCircle className="w-4 h-4" />
                                                                    <span className="text-sm">{t('registration.account.password_mismatch')}</span>
                                                                </div>
                                                            )
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="space-y-4 md:space-y-6">
                                            <div className={`bg-gradient-to-r from-primary/5 to-accent/5 dark:from-blue-500/10 dark:to-purple-500/10 p-4 md:p-6 rounded-lg border ${validationErrors.agreeToTerms ? 'border-red-500' : 'border-primary/20 dark:border-blue-500/30'}`}>
                                                <div className="flex items-start space-x-3 md:space-x-4">
                                                    <Checkbox
                                                        id="terms"
                                                        checked={formData.agreeToTerms}
                                                        onCheckedChange={(checked) => handleInputChange("agreeToTerms", checked as boolean)}
                                                        required
                                                        className="w-4 h-4 md:w-5 md:h-5 mt-1 border-yellow-500 dark:border-gray-500 peer ring-2 ring-transparent peer-checked:ring-blue-500"
                                                    />
                                                    <Label
                                                        htmlFor="terms"
                                                        className="text-xs md:text-base leading-relaxed font-medium dark:text-gray-200"
                                                    >
                                                        {t('registration.terms')}
                                                    </Label>
                                                </div>
                                                <FieldError field="agreeToTerms" />
                                            </div>
                                        </div>

                                        <div className="pt-4 md:pt-6">
                                            <Button
                                                type="submit"
                                                size="lg"
                                                className={`w-full h-12 md:h-14 text-sm md:text-lg font-semibold bg-gradient-to-r from-primary to-primary hover:from-primary/90 hover:to-green/600 dark:from-blue-600 dark:to-green-600 transition-all duration-300 transform hover:scale-[1.02] shadow-lg ${submitDisabled ? "opacity-80 cursor-not-allowed" : ""}`}
                                                disabled={submitDisabled || loading}
                                                aria-disabled={submitDisabled || loading}
                                            >
                                                {loading ? (
                                                    <>
                                                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                                        {t('registration.submit_loading')}
                                                    </>
                                                ) : submitDisabled ? (
                                                    <>
                                                        <Lock className="w-5 h-5 md:w-6 md:h-6 mr-2 md:mr-3" />
                                                        {t('registration.submit_incomplete')}
                                                    </>
                                                ) : (
                                                    <>
                                                        <Users className="w-5 h-5 md:w-6 md:h-6 mr-2 md:mr-3" />
                                                        <span className="hidden sm:inline">{t('registration.submit_ready')}</span>
                                                        <span className="sm:hidden">{t('registration.submit_ready_mobile')}</span>
                                                    </>
                                                )}
                                            </Button>

                                            {successMessage && (
                                                <p className="text-center text-green-600 dark:text-green-400 font-medium mt-3 animate-in fade-in">{successMessage}</p>
                                            )}

                                            {serverError && (
                                                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-3 mt-5">
                                                    <XCircle className="w-5 h-5 mt-0.5 shrink-0" />
                                                    <div>
                                                        <p className="font-semibold">{t('registration.form_title')}</p>
                                                        <p className="text-sm">{serverError}</p>
                                                    </div>
                                                </div>
                                            )}

                                            <p className="text-center text-xs md:text-sm text-muted-foreground dark:text-gray-400 mt-3">
                                                {t('registration.footer_note')}
                                            </p>
                                        </div>
                                    </form>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <RegistrationFooter />
        </div>
    )
}